/**
 * introBank — where the player gets the pieces of an assembled presentation
 * intro (Tom 2026-10-11, "Presentation intros are assembled by the PLAYER").
 *
 * An English-known intro is spoken as separate Charlotte clips:
 *   [The <Base language> for] [the LEGO's own prompt clip] [is]  (+ [as in] [context])
 * The first, third and fourth come from this BANK: per slot a list of
 * variants, and the player picks one. Today every slot holds one variant, so
 * every intro sounds the same; adding a variant to the data file is all it
 * takes to make them vary. The data is written by tools/intro-bank/build-bank.mjs
 * (clips made through Popty's one audio door).
 *
 * Base language only: spa_mx_for_eng → spa → "The Spanish for".
 */
import bankData from './introBank.data.json'
import tomBankData from './introBank.tom.data.json'

export interface BankVariant {
  /** course_audio id — played through /api/audio/:id like every other clip. */
  id: string
  text: string
}

export interface IntroBank {
  /** The voice the pieces are in; the course's known voice must be this one. */
  voiceId: string
  is: BankVariant[]
  asIn: BankVariant[]
  /** Base language code (spa, deu, …) → "The <Language> for" variants. */
  langFor: Record<string, BankVariant[]>
}

export const INTRO_BANK: IntroBank = bankData as IntroBank

/**
 * The same slots in Tom-006's voice (job #310, Tom 2026-10-13): one "The <Language> for" per
 * language, "is" (a Tom clip that already existed) and "as in". TEST ROUND — only the courses
 * listed are covered; reached only through `?introAssemble=tom`.
 */
export const TOM_INTRO_BANK: IntroBank = tomBankData as IntroBank

/** spa_mx_for_eng → 'spa'; non-English-known courses → null (no assembly). */
export function baseLanguageOfCourse(courseCode: string | null | undefined): string | null {
  if (!courseCode || !courseCode.endsWith('_for_eng')) return null
  const base = courseCode.split('_')[0]
  return base || null
}

/** FNV-1a — a small stable hash, so a LEGO always draws the same variant. */
function hashString(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/**
 * The player picks. Seeded by the LEGO so the same intro replays identically
 * (the assembled clip is memoised on its pieces, so a stable pick also means
 * a replay is free); different LEGOs spread across the variants.
 */
export function pickVariant(variants: BankVariant[] | undefined, seed: string): BankVariant | null {
  if (!variants || variants.length === 0) return null
  return variants[hashString(seed) % variants.length]
}
